import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { C } from '../lib/theme';

const s = {
  page: { minHeight: '100vh', background: C.cream, fontFamily: 'sans-serif', padding: '1rem' },
  wrap: { maxWidth: 480, margin: '0 auto', paddingBottom: '2rem' },
  progress: { marginBottom: '2rem' },
  progressBar: { height: 6, background: C.mintLt, borderRadius: 4, overflow: 'hidden', marginTop: 6 },
  progressFill: { height: '100%', background: C.sage, borderRadius: 4, transition: 'width 0.4s' },
  stepText: { color: C.gray, fontSize: '0.8rem', marginBottom: 4 },
  card: { background: '#fff', borderRadius: 16, padding: '1.8rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' },
  h2: { fontFamily: 'Georgia, serif', color: C.sage, fontSize: '1.4rem', margin: '0 0 1.2rem' },
  label: { display: 'block', color: C.carbon, fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem', marginTop: '1rem' },
  input: { width: '100%', padding: '0.7rem 1rem', borderRadius: 8, border: `1.5px solid ${C.mint}`, fontSize: '1rem', boxSizing: 'border-box', background: C.cream, color: C.carbon, outline: 'none' },
  select: { width: '100%', padding: '0.7rem 1rem', borderRadius: 8, border: `1.5px solid ${C.mint}`, fontSize: '1rem', boxSizing: 'border-box', background: C.cream, color: C.carbon, outline: 'none' },
  row: { display: 'flex', gap: '1rem' },
  halfWrap: { flex: 1 },
  btnRow: { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
  btnPrimary: { flex: 1, padding: '0.85rem', borderRadius: 8, border: 'none', background: C.sage, color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer' },
  btnSecondary: { flex: 1, padding: '0.85rem', borderRadius: 8, border: `1.5px solid ${C.mint}`, background: '#fff', color: C.sage, fontSize: '1rem', fontWeight: 600, cursor: 'pointer' },
  notice: { borderRadius: 10, padding: '1rem', fontSize: '0.9rem', marginBottom: '1rem' },
  checkbox: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginTop: '1rem' },
  checkboxInput: { marginTop: 3, accentColor: C.sage, width: 18, height: 18, cursor: 'pointer' },
};

const MEDICATIONS = [
  'Semaglutida (Ozempic / Wegovy)',
  'Tirzepatida (Mounjaro / Zepbound)',
  'Liraglutida (Saxenda)',
  'Dulaglutida (Trulicity)',
  'Otro',
];

const PERSONAS = [
  { value: 'young_female', emoji: '🌱' },
  { value: 'menopause',    emoji: '🌿' },
  { value: 'young_male',   emoji: '💪' },
  { value: 'pre_surgery',  emoji: '🩺' },
];

const TOTAL_STEPS = 6;

function calcBMI(weightKg, heightCm) {
  const h = heightCm / 100;
  return weightKg / (h * h);
}

export default function OnboardingPage({ userId, clinicId, onComplete }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [bmiNotice, setBmiNotice] = useState(null);
  const [showBmiWarning, setShowBmiWarning] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [clinicAccepted, setClinicAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clinicName, setClinicName] = useState(null);

  const [form, setForm] = useState({
    name: '', age: '', units: 'kg/cm', persona: '',
    weight: '', goalWeight: '', height: '', targetDate: '',
    medication: '', diet: '', intolerances: [],
    cooking_style: 'tradicional', injection_day: null,
  });

  // El código canjeado antes de llegar aquí (LandingChat) ya ha vinculado
  // clinic_id en el perfil — solo necesitamos el nombre para mostrarlo.
  useEffect(() => {
    if (!clinicId) return;
    supabase.from('clinics').select('name').eq('id', clinicId).single()
      .then(({ data }) => { if (data) setClinicName(data.name); })
      .catch(() => {});
  }, [clinicId]);

  const INJECTION_DAYS = [
    { value: 1, label: t('onboarding.inj_mon') },
    { value: 2, label: t('onboarding.inj_tue') },
    { value: 3, label: t('onboarding.inj_wed') },
    { value: 4, label: t('onboarding.inj_thu') },
    { value: 5, label: t('onboarding.inj_fri') },
    { value: 6, label: t('onboarding.inj_sat') },
    { value: 0, label: t('onboarding.inj_sun') },
  ];

  const DIETS = [
    { emoji: '🫒', value: 'Mediterránea',          label: t('onboarding.diet_mediterranean') },
    { emoji: '🌶️', value: 'Latinoamericana',        label: t('onboarding.diet_latin') },
    { emoji: '🍛', value: 'Asiática / India',       label: t('onboarding.diet_asian') },
    { emoji: '🥐', value: 'Noreuropea',             label: t('onboarding.diet_northern') },
    { emoji: '🥗', value: 'Vegetariana / Vegana',   label: t('onboarding.diet_vegetarian') },
    { emoji: '☪️', value: 'Halal',                  label: t('onboarding.diet_halal') },
    { emoji: '🌍', value: 'Variada internacional',  label: t('onboarding.diet_international') },
  ];

  const INTOLERANCES = [
    { value: 'Lactosa',           label: t('onboarding.intol_lactose') },
    { value: 'Gluten',            label: t('onboarding.intol_gluten') },
    { value: 'Frutos secos',      label: t('onboarding.intol_nuts') },
    { value: 'Marisco',           label: t('onboarding.intol_shellfish') },
    { value: 'Huevo',             label: t('onboarding.intol_egg') },
    { value: 'Sin intolerancias', label: t('onboarding.intol_none') },
  ];

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleIntolerance = (val) => {
    setForm(f => {
      if (val === 'Sin intolerancias') return { ...f, intolerances: ['Sin intolerancias'] };
      const current = f.intolerances.filter(i => i !== 'Sin intolerancias');
      return { ...f, intolerances: current.includes(val) ? current.filter(i => i !== val) : [...current, val] };
    });
  };

  const nextStep = () => {
    if (step === 2) {
      const weightKg = form.units === 'lb/in' ? parseFloat(form.weight) * 0.453592 : parseFloat(form.weight);
      const heightCm = form.units === 'lb/in' ? parseFloat(form.height) * 2.54 : parseFloat(form.height);
      const bmi = calcBMI(weightKg, heightCm);

      if (!showBmiWarning) {
        if (bmi < 27) {
          setBmiNotice({ type: 'low', bmi: bmi.toFixed(1) });
          setShowBmiWarning(true);
          return;
        } else if (bmi >= 27 && bmi <= 30) {
          setBmiNotice({ type: 'mid', bmi: bmi.toFixed(1) });
          setShowBmiWarning(true);
          return;
        }
      }
    }
    setShowBmiWarning(false);
    setBmiNotice(null);
    setStep(s => s + 1);
  };

  const prevStep = () => setStep(s => s - 1);

  async function handleFinish() {
    if (!accepted || !clinicAccepted) return;
    setSaving(true);
    try {
      const weightKg = form.units === 'lb/in' ? parseFloat(form.weight) * 0.453592 : parseFloat(form.weight);
      const heightCm = form.units === 'lb/in' ? parseFloat(form.height) * 2.54 : parseFloat(form.height);
      const goalKg = form.units === 'lb/in' ? parseFloat(form.goalWeight) * 0.453592 : parseFloat(form.goalWeight);

      const profileData = {
        id: userId,
        role: 'patient',
        name: form.name || '',
        age: parseInt(form.age) || 0,
        units: form.units,
        persona: form.persona || null,
        weight_start: weightKg || 0,
        weight_current: weightKg || 0,
        weight_goal: goalKg || 0,
        height: heightCm || 0,
        target_date: form.targetDate || null,
        drug: form.medication || '',
        diet: form.diet || '',
        intolerances: form.intolerances,
        cooking_style: form.cooking_style,
        injection_day: form.injection_day,
        onboarding_done: true,
        referral_shares_left: 3,
      };

      // clinic_id/is_clinic_patient/clinic_confirmed ya se establecieron en
      // el servidor al canjear el código (antes de llegar aquí) — no se tocan.
      const { error } = await supabase.from('profiles').upsert(profileData);
      if (error) console.error('Upsert error:', error);

      if (!isNaN(weightKg) && weightKg > 0) {
        await supabase.from('weight_logs').insert({ user_id: userId, weight: weightKg });
      }
    } catch (err) {
      console.error('Onboarding save error:', err);
    } finally {
      setSaving(false);
      onComplete();
    }
  }

  const pct = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  return (
    <div style={s.page}>
      <div style={s.wrap}>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '2rem' }}>🌿</span>
          <h2 style={{ ...s.h2, margin: '0.2rem 0 0', textAlign: 'center' }}>Vera GLP</h2>
        </div>

        <div style={s.progress}>
          <div style={s.stepText}>{t('onboarding.step_progress', { step, total: TOTAL_STEPS })}</div>
          <div style={s.progressBar}>
            <div style={{ ...s.progressFill, width: `${pct}%` }} />
          </div>
        </div>

        {step === 1 && (
          <div style={s.card}>
            <h2 style={s.h2}>{t('onboarding.persona_intro')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              {PERSONAS.map(p => (
                <div
                  key={p.value}
                  onClick={() => update('persona', p.value)}
                  style={{
                    padding: '0.8rem 0.7rem', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                    background: form.persona === p.value ? C.mintLt : '#F8F4EF',
                    border: `2px solid ${form.persona === p.value ? C.sage : C.mint}`,
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontSize: '1.4rem', marginBottom: '0.3rem' }}>{p.emoji}</div>
                  <div style={{ fontSize: '0.82rem', color: C.carbon, lineHeight: 1.35 }}>{t(`onboarding.persona_${p.value}`)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div style={s.card}>
            <h2 style={s.h2}>{t('onboarding.step1_title')}</h2>
            <label style={s.label}>{t('onboarding.name_label')}</label>
            <input style={s.input} value={form.name} onChange={e => update('name', e.target.value)} placeholder={t('onboarding.name_placeholder')} />
            <label style={s.label}>{t('onboarding.age_label')}</label>
            <input style={s.input} type="number" value={form.age} onChange={e => update('age', e.target.value)} placeholder={t('onboarding.age_placeholder')} />
            <label style={s.label}>{t('onboarding.units_label')}</label>
            <select style={s.select} value={form.units} onChange={e => update('units', e.target.value)}>
              <option value="kg/cm">kg / cm</option>
              <option value="lb/in">lb / in</option>
            </select>
          </div>
        )}

        {step === 2 && !showBmiWarning && (
          <div style={s.card}>
            <h2 style={s.h2}>{t('onboarding.step2_title')}</h2>
            <div style={s.row}>
              <div style={s.halfWrap}>
                <label style={s.label}>{t('onboarding.weight_label', { unit: form.units.split('/')[0] })}</label>
                <input style={s.input} type="number" value={form.weight} onChange={e => update('weight', e.target.value)} placeholder="Ej. 90" />
              </div>
              <div style={s.halfWrap}>
                <label style={s.label}>{t('onboarding.goal_label', { unit: form.units.split('/')[0] })}</label>
                <input style={s.input} type="number" value={form.goalWeight} onChange={e => update('goalWeight', e.target.value)} placeholder="Ej. 75" />
              </div>
            </div>
            <label style={s.label}>{t('onboarding.height_label', { unit: form.units.split('/')[1] })}</label>
            <input style={s.input} type="number" value={form.height} onChange={e => update('height', e.target.value)} placeholder={form.units === 'kg/cm' ? t('onboarding.height_placeholder_metric') : t('onboarding.height_placeholder_imperial')} />

            <label style={s.label}>{t('onboarding.target_date_label')}</label>
            <input
              style={s.input}
              type="date"
              min={new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]}
              value={form.targetDate}
              onChange={e => update('targetDate', e.target.value)}
            />
            <p style={{ fontSize: '0.8rem', color: C.gray, marginTop: '0.8rem' }}>
              {t('onboarding.target_date_note')}
            </p>
          </div>
        )}

        {step === 2 && showBmiWarning && bmiNotice && (
          <div style={s.card}>
            <div style={{
              ...s.notice,
              background: bmiNotice.type === 'low' ? '#FEF3C7' : '#FEE2E2',
              border: `1px solid ${bmiNotice.type === 'low' ? '#F59E0B' : C.terra}`,
            }}>
              <strong>{bmiNotice.type === 'low' ? t('onboarding.bmi_low_title') : t('onboarding.bmi_mid_title')}</strong>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.88rem' }}>
                {bmiNotice.type === 'low'
                  ? t('onboarding.bmi_low_text', { bmi: bmiNotice.bmi })
                  : t('onboarding.bmi_mid_text', { bmi: bmiNotice.bmi })
                }
              </p>
            </div>
            <button style={s.btnPrimary} onClick={() => { setShowBmiWarning(false); setBmiNotice(null); setStep(3); }}>
              {t('onboarding.bmi_understood')}
            </button>
          </div>
        )}

        {step === 3 && (
          <div style={s.card}>
            <h2 style={s.h2}>{t('onboarding.step3_title')}</h2>
            <label style={s.label}>{t('onboarding.medication_label')}</label>
            {MEDICATIONS.map(med => (
              <div
                key={med}
                onClick={() => update('medication', med)}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: 8,
                  marginTop: '0.5rem',
                  cursor: 'pointer',
                  background: form.medication === med ? C.mintLt : '#F8F4EF',
                  border: `2px solid ${form.medication === med ? C.sage : C.mint}`,
                  color: C.carbon,
                  fontWeight: form.medication === med ? 700 : 400,
                  transition: 'all 0.2s',
                }}
              >
                {med}
              </div>
            ))}

            <label style={s.label}>{t('onboarding.injection_day_label')}</label>
            <p style={{ fontSize: '0.78rem', color: C.gray, margin: '0.2rem 0 0.6rem' }}>
              {t('onboarding.injection_day_note')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.3rem' }}>
              {INJECTION_DAYS.map(d => (
                <div
                  key={d.value}
                  onClick={() => update('injection_day', d.value)}
                  style={{
                    padding: '0.5rem 0.2rem', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                    background: form.injection_day === d.value ? C.sage : '#F8F4EF',
                    color: form.injection_day === d.value ? '#fff' : C.carbon,
                    border: `2px solid ${form.injection_day === d.value ? C.sage : C.mint}`,
                    fontSize: '0.78rem', fontWeight: form.injection_day === d.value ? 700 : 400,
                    transition: 'all 0.15s',
                  }}
                >
                  {d.label}
                </div>
              ))}
            </div>
            {form.injection_day === null && (
              <p style={{ fontSize: '0.75rem', color: C.gray, marginTop: '0.4rem' }}>{t('onboarding.injection_optional')}</p>
            )}
          </div>
        )}

        {step === 4 && (
          <div style={s.card}>
            <h2 style={s.h2}>{t('onboarding.step4_title')}</h2>

            <label style={s.label}>{t('onboarding.cooking_label')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              {[
                { value: 'tradicional', emoji: '🫕', labelKey: 'traditional_label', descKey: 'traditional_desc' },
                { value: 'moderno',     emoji: '🌟', labelKey: 'modern_label',       descKey: 'modern_desc' },
              ].map(opt => (
                <div
                  key={opt.value}
                  onClick={() => update('cooking_style', opt.value)}
                  style={{
                    padding: '0.85rem 0.7rem', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                    background: form.cooking_style === opt.value ? C.mintLt : '#F8F4EF',
                    border: `2px solid ${form.cooking_style === opt.value ? C.sage : C.mint}`,
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontSize: '1.5rem' }}>{opt.emoji}</div>
                  <div style={{ fontWeight: 700, color: C.carbon, fontSize: '0.9rem' }}>{t(`onboarding.${opt.labelKey}`)}</div>
                  <div style={{ fontSize: '0.72rem', color: C.gray, marginTop: '0.2rem', lineHeight: 1.3 }}>{t(`onboarding.${opt.descKey}`)}</div>
                </div>
              ))}
            </div>

            <label style={s.label}>{t('onboarding.diet_label')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
              {DIETS.map(d => (
                <div
                  key={d.value}
                  onClick={() => update('diet', d.value)}
                  style={{
                    padding: '0.75rem',
                    borderRadius: 8,
                    cursor: 'pointer',
                    textAlign: 'center',
                    background: form.diet === d.value ? C.mintLt : '#F8F4EF',
                    border: `2px solid ${form.diet === d.value ? C.sage : C.mint}`,
                    fontSize: '0.9rem',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontSize: '1.4rem' }}>{d.emoji}</div>
                  {d.label}
                </div>
              ))}
            </div>

            <label style={s.label}>{t('onboarding.intol_label')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              {INTOLERANCES.map(i => (
                <div
                  key={i.value}
                  onClick={() => toggleIntolerance(i.value)}
                  style={{
                    padding: '0.5rem 0.9rem',
                    borderRadius: 20,
                    cursor: 'pointer',
                    background: form.intolerances.includes(i.value) ? C.sage : '#F8F4EF',
                    color: form.intolerances.includes(i.value) ? '#fff' : C.carbon,
                    border: `1.5px solid ${form.intolerances.includes(i.value) ? C.sage : C.mint}`,
                    fontSize: '0.85rem',
                    transition: 'all 0.2s',
                  }}
                >
                  {i.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <div style={{ ...s.card, border: '2px solid #D1FAE5' }}>
              <h3 style={{ color: C.sage, margin: '0 0 0.8rem', fontFamily: 'Georgia, serif' }}>{t('onboarding.step5_does_title')}</h3>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem', color: C.carbon, lineHeight: 1.7 }}>
                {['does_1','does_2','does_3','does_4'].map(k => <li key={k}>{t(`onboarding.${k}`)}</li>)}
              </ul>
            </div>
            <div style={{ ...s.card, border: `2px solid ${C.terra}` }}>
              <h3 style={{ color: C.terra, margin: '0 0 0.8rem', fontFamily: 'Georgia, serif' }}>{t('onboarding.step5_does_not_title')}</h3>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem', color: C.carbon, lineHeight: 1.7 }}>
                {['does_not_1','does_not_2','does_not_3','does_not_4'].map(k => <li key={k}>{t(`onboarding.${k}`)}</li>)}
              </ul>
            </div>
            <div style={{ ...s.card, border: '2px solid #E5E7EB' }}>
              <h3 style={{ color: C.gray, margin: '0 0 0.8rem', fontFamily: 'Georgia, serif' }}>{t('onboarding.step5_data_title')}</h3>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem', color: C.carbon, lineHeight: 1.7 }}>
                {['data_1','data_2','data_3'].map(k => <li key={k}>{t(`onboarding.${k}`)}</li>)}
              </ul>
            </div>
            <div style={s.checkbox}>
              <input type="checkbox" id="consent" checked={accepted} onChange={e => setAccepted(e.target.checked)} style={s.checkboxInput} />
              <label htmlFor="consent" style={{ fontSize: '0.88rem', color: C.carbon, cursor: 'pointer' }}>
                {t('onboarding.consent_text')}
              </label>
            </div>
          </div>
        )}

        {step === 6 && (
          <div>
            <div style={{ ...s.card, border: `2px solid ${C.mint}` }}>
              <h3 style={{ color: C.sage, margin: '0 0 0.8rem', fontFamily: 'Georgia, serif' }}>
                {t('onboarding.clinic_step_title')}
              </h3>
              <p style={{ color: C.carbon, fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 0.8rem' }}>
                {t('onboarding.clinic_patient_intro')} <strong>{clinicName || t('onboarding.your_clinic')}</strong>.
              </p>
              <ul style={{ margin: '0 0 1rem', paddingLeft: '1.2rem', fontSize: '0.88rem', color: C.carbon, lineHeight: 1.8 }}>
                <li>{t('onboarding.clinic_bullet_1')}</li>
                <li>{t('onboarding.clinic_bullet_2')}</li>
                <li>{t('onboarding.clinic_bullet_3')}</li>
              </ul>
              <p style={{ color: C.gray, fontSize: '0.82rem', margin: 0 }}>
                {t('onboarding.clinic_safety_note')}
              </p>
            </div>
            <div style={s.checkbox}>
              <input type="checkbox" id="clinicConsent" checked={clinicAccepted} onChange={e => setClinicAccepted(e.target.checked)} style={s.checkboxInput} />
              <label htmlFor="clinicConsent" style={{ fontSize: '0.88rem', color: C.carbon, cursor: 'pointer' }}>
                {t('onboarding.clinic_consent_1')} <strong>{clinicName || t('onboarding.my_clinic')}</strong> {t('onboarding.clinic_consent_2')}
              </label>
            </div>
          </div>
        )}

        {!showBmiWarning && (
          <div style={s.btnRow}>
            {step > 1 && <button style={s.btnSecondary} onClick={prevStep}>{t('common.back')}</button>}
            {step < TOTAL_STEPS
              ? <button style={s.btnPrimary} onClick={nextStep}>{t('common.continue')}</button>
              : <button
                  style={{ ...s.btnPrimary, opacity: (accepted && clinicAccepted) ? 1 : 0.5 }}
                  onClick={handleFinish}
                  disabled={!accepted || !clinicAccepted || saving}
                >
                  {saving ? t('common.saving') : t('onboarding.start_btn')}
                </button>
            }
          </div>
        )}
      </div>
    </div>
  );
}
